import React, { FC, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { ANALYTICS_EVENTS } from 'constants/analyticsEvents';
import {
  TEST_ADD_TO_LIST_CREATE_LIST_BTN,
  TEST_ADD_TO_LIST_TITLE,
  TEST_ID_ADD_TO_LIST_START,
} from 'constants/aqa/addPick';
import { ALL_PICKS } from 'constants/common';
import { CardSchema, CardTypeOptions } from 'constants/graphqlTypes';
import { generateTopicTitle } from 'constants/aqa/signup';
import {
  ROUTE_ADD_SAVED_CONTENT,
  ROUTE_ADD_SAVED_CONTENT_CHOOSE_LIST,
  ROUTE_ADD_SAVED_CONTENT_CREATE_LIST,
  ROUTE_ADD_STYLE_AND_THOUGHT,
  ROUTE_ADD_TO_LIST,
} from 'routes';
import { useLink } from 'helpers/routingHelper';
import { getListStyles } from 'components/Collection/helpers';
import { getGradientImage } from 'components/Sparks/constants';
import { useGetLists } from 'graphQL/addContent/addToList/hooks';
import { useListsWithCard } from 'graphQL/list/hooks';
import { useGetUserSparks } from 'graphQL/sparks/hooks';
import getAuthUser from 'helpers/getAuthUser';
import { useAnalytics } from 'contexts/AnalyticsContext';
import useGuideTour from 'helpers/useGuideTour';

import LoaderContent from 'components/UI/LoaderContent';
import UiButton from 'components/UI/Button';
import { EUiButtonType } from 'components/UI/Button/constants';
import Button from 'components/UI/MenuButton';
import CardsMiniaturePile from 'components/CardsMiniaturePile';
import ConfirmationModal from 'components/UI/ConfirmationModal';
import { ReactComponent as ChevronLeft } from 'assets/icons/ChevronLeft.svg';
import { ReactComponent as SearchIcon } from 'assets/icons/Search.svg';
import { ReactComponent as ListIcon } from 'assets/icons/List.svg';

import { useGetCoverPickPreview, useAddContentLinks } from '../helpers/hooks';
import { IAddContentLocation } from '../helpers/models';
import { IChooseListToShareProps } from './models';

import {
  StyledContainer,
  StyledModalContent,
  StyledViewControls,
  StyledBackButton,
  StyledPicksThumbnails,
  StyledThumbnailWrapper,
  StyledThumbnailImage,
  StyledCountBadge,
  StyledHeader,
  StyledTitle,
  StyledSearchContainer,
  StyledSearchInput,
  StyledSearchInputField,
  StyledListContainer,
  StyledListSection,
  StyledSectionHeader,
  StyledSectionTitle,
  StyledListItem,
  StyledItemImage,
  StyledItemImageFallback,
  StyledItemImageStack,
  StyledItemContent,
  StyledItemTitle,
  StyledItemSubtitle,
  StyledPrivateLabel,
  StyledSpacing,
  StyledEmptyButton,
  StyledMenuButtonContainer,
  StyledConfirmationWrapper,
  StyledShowMoreButton,
} from './styled';

interface ListItemDisplayProps {
  item: CardSchema;
  isDrafts?: boolean;
  isAlreadyAdded?: boolean;
  onSelect: () => void;
  dataTestId?: string;
}

const ListItemDisplay: FC<ListItemDisplayProps> = ({ item, isDrafts, isAlreadyAdded, onSelect, dataTestId }) => {
  const { title, isPrivateList } = item;
  const { urls } = getListStyles(item);
  const hasImages = urls.length > 0;
  const hasMultipleImages = urls.length > 1;
  const showSubtitle = isDrafts || isAlreadyAdded;
  const borderRadius = isDrafts ? 10 : 8;

  const imageElement = hasMultipleImages ? (
    <StyledItemImageStack borderRadius={borderRadius}>
      {urls.slice(0, 3).map((url) => (
        <img key={`${item.id}-img-${url}`} src={url} alt="" />
      ))}
    </StyledItemImageStack>
  ) : (
    <StyledItemImage borderRadius={borderRadius}>
      <img src={urls[0]} alt="" />
    </StyledItemImage>
  );

  return (
    <StyledListItem onClick={onSelect} data-testid={dataTestId}>
      {!hasImages ? <StyledItemImageFallback borderRadius={borderRadius} /> : imageElement}
      <StyledItemContent>
        <StyledItemTitle>{isDrafts ? 'Share later' : title}</StyledItemTitle>
        {showSubtitle && <StyledItemSubtitle>{isDrafts ? 'Drafts' : 'Added already'}</StyledItemSubtitle>}
      </StyledItemContent>
      {isPrivateList && <StyledPrivateLabel>Private</StyledPrivateLabel>}
    </StyledListItem>
  );
};

const ChooseListToShare: FC<IChooseListToShareProps> = ({
  pickId,
  handleClose,
  handlePrev,
  setListToAdd,
  setListRoutingParams,
  setListTitle,
  setExtractData,
  cardsPileData,
}) => {
  const { replace } = useHistory();
  const { track } = useAnalytics();
  const { state, pathname } = useLocation<IAddContentLocation | undefined>();
  const [searchValue, setSearchValue] = useState('');
  const [showExitWarning, setShowExitWarning] = useState(false);
  const [isExitConfirmed, setIsExitConfirmed] = useState(false);
  const [listsExpanded, setListsExpanded] = useState(false);
  const [sparksExpanded, setSparksExpanded] = useState(false);

  const { addStyle, addList, addThought, home, addPick, niceFind } = useAddContentLinks();

  const goToAddStyleAndThought = (
    targetListCardId?: string,
    targetListTitle?: string,
    targetListId?: string,
    targetIsPrivateList?: boolean,
    targetIsSpark?: boolean
  ) => {
    // Set parent state for components that read from props
    setListTitle(targetListTitle ?? '');
    setListToAdd(targetListCardId ?? '');
    setListRoutingParams({ id: targetListId ?? '', isPrivateList: !!targetIsPrivateList, isSpark: !!targetIsSpark });

    replace({
      pathname: ROUTE_ADD_STYLE_AND_THOUGHT,
      state: {
        ...state,
        variable: {
          ...state?.variable,
          prevPath: ROUTE_ADD_TO_LIST,
          // Target list info
          listCardId: targetListCardId,
          listTitle: targetListTitle,
          listId: targetListId,
          isPrivateList: targetIsPrivateList,
          isSpark: targetIsSpark,
        },
      },
    });
  };

  const addSavedContentLink = useLink(ROUTE_ADD_SAVED_CONTENT);
  const addSavedContentCreateListLink = useLink(ROUTE_ADD_SAVED_CONTENT_CREATE_LIST);

  const isSavedContentChooseList = pathname === ROUTE_ADD_SAVED_CONTENT_CHOOSE_LIST;
  const { variable: stateData } = state ?? {};
  const {
    pickId: statePickId,
    cardId: stateCardId,
    isPickFlow,
    isDetail,
    isMovePick,
    isSavedPick,
    isFromNiceFind,
    cardImage,
  } = stateData ?? {};
  const itemId = statePickId ?? pickId;
  const { items, loading, loadMore } = useGetLists(itemId);

  // Load all items upfront (replaces Waypoint infinite scroll)
  useEffect(() => {
    if (!loading && items.length > 0) {
      loadMore();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length, loading]);
  const { listCardIdsWithCard } = useListsWithCard(itemId);
  const { isGuideTour } = useGuideTour();

  // Fetch user's sparks
  const { userId } = getAuthUser();
  const { items: sparkItems, loading: sparksLoading } = useGetUserSparks({ userId });
  const sparks = isGuideTour ? [] : sparkItems;

  const handleNext = (
    targetListCardId?: string,
    targetListTitle?: string,
    targetListId?: string,
    targetIsPrivateList?: boolean,
    targetIsSpark?: boolean
  ) => {
    if (isFromNiceFind) {
      goToAddStyleAndThought(targetListCardId, targetListTitle, targetListId, targetIsPrivateList, targetIsSpark);
    } else if (isSavedContentChooseList) {
      replace(addSavedContentLink);
    } else if (isSavedPick) {
      replace(addStyle);
    } else {
      const nextRoute = isPickFlow || isDetail || isMovePick ? addThought : addStyle;
      replace(nextRoute);
    }
  };

  const lists = isGuideTour ? [] : items;

  // Sort and organize lists into sections
  const { draftsItem, filteredLists, filteredSparks } = useMemo(() => {
    const drafts = lists.find((item) => item.type === CardTypeOptions.Drafts);
    const nonDrafts = lists.filter((item) => item.type !== CardTypeOptions.Drafts);

    const searchFiltered = searchValue
      ? nonDrafts.filter((item) => item.title?.toLowerCase().includes(searchValue.toLowerCase()))
      : nonDrafts;

    const sparksFiltered = searchValue
      ? sparks.filter((item) => item.title?.toLowerCase().includes(searchValue.toLowerCase()))
      : sparks;

    return {
      draftsItem: drafts,
      filteredLists: searchFiltered,
      filteredSparks: sparksFiltered,
    };
  }, [lists, sparks, searchValue]);

  const SECTION_LIMIT = 3;
  const isSearching = searchValue.length > 0;

  const visibleLists = isSearching || listsExpanded ? filteredLists : filteredLists.slice(0, SECTION_LIMIT);
  const visibleSparks = isSearching || sparksExpanded ? filteredSparks : filteredSparks.slice(0, SECTION_LIMIT);

  const showListsToggle = !isSearching && filteredLists.length > SECTION_LIMIT;
  const showSparksToggle = !isSearching && filteredSparks.length > SECTION_LIMIT;

  useEffect(() => {
    if (!itemId) {
      replace(home);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId]);

  useEffect(() => {
    if (stateCardId && statePickId) {
      setExtractData?.({ extractPickId: statePickId, extractCardId: stateCardId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateCardId, statePickId]);

  useEffect(() => {
    if (isExitConfirmed) {
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExitConfirmed]);

  const handleItemClick = (title: string, listCardId?: string, id = '', isPrivateList = false, isSpark = false) => {
    setListTitle(title);
    setListToAdd(listCardId ?? '');
    setListRoutingParams({ id, isPrivateList, isSpark });
    handleNext(listCardId, title, id, isPrivateList, isSpark);
  };

  const handleCreateListClick = () => {
    if (isSavedContentChooseList) {
      replace(addSavedContentCreateListLink);
    } else if (isSavedPick) {
      replace({ ...addList, state: { ...addList.state, isSavedPick } });
    } else {
      replace({ ...addList, state: { ...addList.state, isPickFlow } });
    }
  };

  const { item, loading: previewLoading } = useGetCoverPickPreview(pickId);
  const { isSavedByMe } = item ?? {};

  const goToPreviousStep = () => {
    if (isFromNiceFind) {
      replace(niceFind);
    } else if (handlePrev) {
      handlePrev();
    } else if (!isDetail && !isSavedByMe) {
      replace(addPick);
    } else {
      handleClose();
    }
  };

  const cardsForPile = cardsPileData || (item ? [item] : []);
  const pickCount = cardsPileData?.length ?? 1;
  const thumbnailImage = cardImage || cardsForPile[0]?.cardStyle?.thumbnailImageSet?.images?.[0]?.url;

  const hasItems = !!lists.length || !!sparks.length;

  return (
    <StyledContainer>
      <StyledModalContent>
        <StyledViewControls>
          <StyledBackButton onClick={goToPreviousStep} aria-label="Go back">
            <ChevronLeft />
          </StyledBackButton>
          {!previewLoading && cardsForPile.length > 0 && (
            <StyledPicksThumbnails>
              <StyledThumbnailWrapper>
                {thumbnailImage ? (
                  <StyledThumbnailImage>
                    <img src={thumbnailImage} alt="Selected pick" />
                  </StyledThumbnailImage>
                ) : (
                  <CardsMiniaturePile cards={cardsForPile} />
                )}
              </StyledThumbnailWrapper>
              <StyledCountBadge>{pickCount}</StyledCountBadge>
            </StyledPicksThumbnails>
          )}
          {!isFromNiceFind ? (
            <UiButton
              text="Continue without list"
              type={EUiButtonType.Primary}
              isDark
              handler={() => {
                handleItemClick(ALL_PICKS);
                track(ANALYTICS_EVENTS.AddPickOrgLt);
              }}
            />
          ) : (
            <StyledEmptyButton />
          )}
        </StyledViewControls>

        <StyledHeader>
          <StyledTitle data-testid={TEST_ADD_TO_LIST_TITLE}>
            {hasItems ? 'Choose a place to share to' : 'Create lists to organize your picks'}
          </StyledTitle>
        </StyledHeader>

        <StyledSearchContainer>
          <StyledSearchInput>
            <SearchIcon />
            <StyledSearchInputField
              type="text"
              placeholder="Search"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />
          </StyledSearchInput>
        </StyledSearchContainer>

        <StyledListContainer>
          {loading || sparksLoading ? (
            <LoaderContent isFullScreen={!items.length} />
          ) : (
            <>
              {/* Drafts section - always first */}
              {draftsItem && !searchValue && (
                <StyledListSection hasTopPadding>
                  <ListItemDisplay
                    item={draftsItem}
                    isDrafts
                    onSelect={() => {
                      handleItemClick(
                        draftsItem.title ?? '',
                        draftsItem.cardId,
                        draftsItem.id,
                        !!draftsItem.isPrivateList
                      );
                      track(
                        draftsItem.isPrivateList ? ANALYTICS_EVENTS.AddPickPrivate : ANALYTICS_EVENTS.AddPickPublic
                      );
                    }}
                    dataTestId={`${TEST_ID_ADD_TO_LIST_START}_item-${generateTopicTitle(draftsItem.title || '')}`}
                  />
                </StyledListSection>
              )}

              {/* Lists section */}
              {visibleLists.length > 0 && (
                <StyledListSection>
                  <StyledSectionHeader>
                    <StyledSectionTitle>Lists</StyledSectionTitle>
                  </StyledSectionHeader>
                  {visibleLists.map((listItem) => (
                    <ListItemDisplay
                      key={listItem.id}
                      item={listItem}
                      isAlreadyAdded={listCardIdsWithCard.includes(listItem.cardId ?? '')}
                      onSelect={() => {
                        handleItemClick(listItem.title ?? '', listItem.cardId, listItem.id, !!listItem.isPrivateList);
                        track(
                          listItem.isPrivateList ? ANALYTICS_EVENTS.AddPickPrivate : ANALYTICS_EVENTS.AddPickPublic
                        );
                      }}
                      dataTestId={`${TEST_ID_ADD_TO_LIST_START}_item-${generateTopicTitle(listItem.title || '')}`}
                    />
                  ))}
                  {showListsToggle && (
                    <StyledShowMoreButton onClick={() => setListsExpanded(!listsExpanded)}>
                      {listsExpanded ? 'Show less' : 'Show more'}
                    </StyledShowMoreButton>
                  )}
                </StyledListSection>
              )}

              {/* Sparks section */}
              {visibleSparks.length > 0 && (
                <StyledListSection>
                  <StyledSectionHeader>
                    <StyledSectionTitle>Sparks</StyledSectionTitle>
                  </StyledSectionHeader>
                  {visibleSparks.map((sparkItem) => {
                    const typedSparkItem = sparkItem as typeof sparkItem & {
                      userCoverSelection?: { mediaUrl?: string; mediaType?: string; thumbnailUrl?: string };
                    };
                    const coverMediaUrl = typedSparkItem.userCoverSelection?.mediaUrl;
                    const thumbnailUrl = typedSparkItem.userCoverSelection?.thumbnailUrl;
                    const isAnimatedCover = typedSparkItem.userCoverSelection?.mediaType === 'ANIMATION';
                    const gradientName = sparkItem.cardStyle?.bgColor?.predefinedGradientName ?? '';
                    const sparkThumbnail = isAnimatedCover
                      ? thumbnailUrl || coverMediaUrl || getGradientImage(gradientName)
                      : coverMediaUrl || getGradientImage(gradientName);

                    return (
                      <StyledListItem
                        key={sparkItem.id}
                        onClick={() => {
                          handleItemClick(
                            sparkItem.title ?? '',
                            sparkItem.cardId,
                            sparkItem.id,
                            !!sparkItem.isPrivateList,
                            true
                          );
                          track(
                            sparkItem.isPrivateList ? ANALYTICS_EVENTS.AddPickPrivate : ANALYTICS_EVENTS.AddPickPublic
                          );
                        }}
                        data-testid={`${TEST_ID_ADD_TO_LIST_START}_spark-${generateTopicTitle(sparkItem.title || '')}`}
                      >
                        <StyledItemImage>
                          <img src={sparkThumbnail} alt="" />
                        </StyledItemImage>
                        <StyledItemContent>
                          <StyledItemTitle>{sparkItem.title}</StyledItemTitle>
                        </StyledItemContent>
                        {sparkItem.isPrivateList && <StyledPrivateLabel>Private</StyledPrivateLabel>}
                      </StyledListItem>
                    );
                  })}
                  {showSparksToggle && (
                    <StyledShowMoreButton onClick={() => setSparksExpanded(!sparksExpanded)}>
                      {sparksExpanded ? 'Show less' : 'Show more'}
                    </StyledShowMoreButton>
                  )}
                </StyledListSection>
              )}
            </>
          )}
          <StyledSpacing />
        </StyledListContainer>

        <StyledMenuButtonContainer>
          <Button
            dataTestId={TEST_ADD_TO_LIST_CREATE_LIST_BTN}
            text="Create a list"
            Icon={ListIcon}
            handler={handleCreateListClick}
          />
        </StyledMenuButtonContainer>
      </StyledModalContent>

      {showExitWarning && (
        <StyledConfirmationWrapper>
          <ConfirmationModal
            handleCancel={() => setShowExitWarning(false)}
            handleClickOutside={() => setShowExitWarning(false)}
            handleAccept={() => setIsExitConfirmed(true)}
            isWarning
            buttonText="Discard"
            titleText="Discard pick?"
            subTitle="Your edits and thoughts will be discarded if you leave before posting."
          />
        </StyledConfirmationWrapper>
      )}
    </StyledContainer>
  );
};

export default ChooseListToShare;
